import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { Alert } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { type Columns, DataTable } from '../../components/ui/data-table';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Checkbox, Input, Select } from '../../components/ui/input';
import { api, errorMessage } from '../../lib/api';
import { can, useMe } from '../../lib/session';
import type { MovementRow, StockLevelRow, StockLocation } from '../../lib/types';
import { formatDateTime } from '../../lib/utils';

export const locationsQuery = {
  queryKey: ['warehouse', 'locations'],
  queryFn: () => api<{ items: StockLocation[] }>('/warehouse/locations'),
};

export function LocationSelect({
  value,
  onChange,
  allowAll = true,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  allowAll?: boolean;
  id?: string;
}) {
  const { t } = useTranslation();
  const locations = useQuery(locationsQuery);
  return (
    <Select
      id={id}
      aria-label={t('wh.location')}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      className="w-56"
    >
      {allowAll ? <option value="">{t('wh.allLocations')}</option> : null}
      {(locations.data?.items ?? []).map((l) => (
        <option key={l.id} value={l.id}>
          {l.code} — {l.name}
        </option>
      ))}
    </Select>
  );
}

export function StockPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const [locationId, setLocationId] = useState('');
  const [q, setQ] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const params = new URLSearchParams();
  if (locationId) params.set('locationId', locationId);
  if (q.trim()) params.set('q', q.trim());
  const stock = useQuery({
    queryKey: ['warehouse', 'stock', locationId, q.trim()],
    queryFn: () => api<{ items: StockLevelRow[] }>(`/warehouse/stock?${params.toString()}`),
  });
  const movements = useQuery({
    queryKey: ['warehouse', 'movements', locationId],
    queryFn: () =>
      api<{ items: MovementRow[] }>(
        `/warehouse/movements${locationId ? `?locationId=${locationId}` : ''}`,
      ),
  });

  const columns = useMemo<Columns<StockLevelRow>>(
    () => [
      { id: 'sku', header: t('wh.sku'), accessorFn: (r) => r.sku },
      { id: 'name', header: t('wh.item'), accessorFn: (r) => r.name },
      { id: 'location', header: t('wh.location'), accessorFn: (r) => r.locationCode },
      { id: 'onHand', header: t('wh.onHand'), accessorFn: (r) => r.onHand },
      { id: 'reserved', header: t('wh.reserved'), accessorFn: (r) => r.reserved },
      { id: 'damaged', header: t('wh.damaged'), accessorFn: (r) => r.damaged },
      { id: 'incoming', header: t('wh.incoming'), accessorFn: (r) => r.incoming },
      {
        id: 'available',
        header: t('wh.available'),
        accessorFn: (r) => r.available,
        cell: ({ row }) => <span className="font-medium">{row.original.available}</span>,
      },
    ],
    [t],
  );
  const movementColumns = useMemo<Columns<MovementRow>>(
    () => [
      {
        id: 'when',
        header: t('wh.when'),
        accessorFn: (r) => r.createdAt,
        cell: ({ row }) => formatDateTime(row.original.createdAt, i18n.language),
      },
      { id: 'sku', header: t('wh.sku'), accessorFn: (r) => r.sku },
      { id: 'location', header: t('wh.location'), accessorFn: (r) => r.locationCode },
      { id: 'type', header: t('wh.movementType'), accessorFn: (r) => t(`wh.movements.${r.type}`) },
      {
        id: 'qty',
        header: t('wh.qty'),
        accessorFn: (r) => r.qty,
        cell: ({ row }) => (
          <span className={row.original.qty > 0 ? 'text-emerald-700' : 'text-destructive'}>
            {row.original.qty > 0 ? '+' : ''}
            {row.original.qty}
          </span>
        ),
      },
      {
        id: 'reason',
        header: t('wh.reason'),
        accessorFn: (r) => r.reason ?? r.referenceType ?? '',
      },
    ],
    [t, i18n.language],
  );

  return (
    <>
      <PageHeader
        title={t('wh.stockTitle')}
        description={t('wh.stockHint')}
        actions={
          can(me, 'inventory.adjust') ? (
            <Button
              onClick={() => {
                setAdjusting(true);
              }}
            >
              {t('wh.adjust')}
            </Button>
          ) : null
        }
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <LocationSelect value={locationId} onChange={setLocationId} />
        <Input
          aria-label={t('wh.searchCode')}
          placeholder={t('wh.searchCode')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
          }}
          className="w-64"
        />
      </div>
      {stock.error ? (
        <Alert variant="destructive" className="mb-4">
          {errorMessage(stock.error)}
        </Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={stock.data?.items ?? []}
        loading={stock.isPending}
        rowKey={(r) => `${r.variantId}:${r.locationId}`}
      />
      <h2 className="mb-3 mt-8 text-lg font-semibold">{t('wh.movementsTitle')}</h2>
      <DataTable
        columns={movementColumns}
        data={movements.data?.items ?? []}
        loading={movements.isPending}
        rowKey={(r) => r.id}
      />
      {adjusting ? (
        <AdjustDialog
          onClose={() => {
            setAdjusting(false);
          }}
        />
      ) : null}
    </>
  );
}

function AdjustDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [locationId, setLocationId] = useState('');
  const [code, setCode] = useState('');
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [damaged, setDamaged] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const locations = useQuery(locationsQuery);
  const location = locationId || locations.data?.items[0]?.id || '';

  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await api('/warehouse/adjustments', {
        method: 'POST',
        body: { locationId: location, code: code.trim(), delta: Number(delta), reason, damaged },
      });
      await queryClient.invalidateQueries({ queryKey: ['warehouse'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('wh.adjust')}
      description={t('wh.adjustHint')}
      footer={
        <Button
          disabled={busy || !code.trim() || !Number(delta) || reason.trim().length < 2}
          onClick={() => void save()}
        >
          {t('common.save')}
        </Button>
      }
    >
      <div className="grid gap-3">
        <Field label={t('wh.location')}>
          {(id) => (
            <LocationSelect id={id} value={location} onChange={setLocationId} allowAll={false} />
          )}
        </Field>
        <Field label={t('wh.code')}>
          {(id) => (
            <Input
              id={id}
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              className="font-mono"
            />
          )}
        </Field>
        <Field label={t('wh.delta')} hint={t('wh.deltaHint')}>
          {(id) => (
            <Input
              id={id}
              type="number"
              value={delta}
              onChange={(e) => {
                setDelta(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('wh.reason')}>
          {(id) => (
            <Input
              id={id}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          )}
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={damaged}
            onChange={(e) => {
              setDamaged(e.target.checked);
            }}
          />
          {t('wh.markDamaged')}
        </label>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}
